"use client";

import { Check, Languages } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link } from "@/i18n/link";
import { LOCALE_FLAGS } from "@/i18n/locale-flags";
import { localeName } from "@/i18n/locale-name";
import type { Locale } from "@/i18n/locale.type";
import { usePathname } from "@/i18n/use-pathname";
import { cn } from "@/lib/utils";
import { useEnabledLanguages } from "../brand-provider/app-brand-provider";
import { useAlternatePaths } from "../alternate-paths/set-alternate-paths";

/** The flag of a language, as a small rounded rectangle. Falls back to a generic icon if none is mapped. */
export function LocaleFlag({ locale, className }: { locale: Locale; className?: string }) {
  const Flag = LOCALE_FLAGS[locale];
  if (!Flag) return <Languages className={className} />;
  return <Flag aria-hidden className={cn("h-3.5 w-auto shrink-0 rounded-[2px]", className)} />;
}

function LocaleOption({ locale, pathname, current }: { locale: Locale; pathname: string; current: boolean }) {
  return (
    <Button
      variant={current ? "secondary" : "ghost"}
      className="justify-between"
      nativeButton={false}
      render={<Link href={pathname} locale={locale} lang={locale} hrefLang={locale} aria-current={current ? "true" : undefined} />}
    >
      <span className="flex items-center gap-2"><LocaleFlag locale={locale} />{localeName(locale)}</span>
      {current && <Check />}
    </Button>
  );
}

export function LocaleSwitcher() {
  const current = useLocale();
  const pathname = usePathname();
  const t = useTranslations("common");
  const own = useAlternatePaths();
  const languages = useEnabledLanguages();
  if (languages.length < 2) return null;

  return (
    <Popover>
      <PopoverTrigger render={<Button variant="ghost" size="icon" aria-label={t("language")} />}>
        <LocaleFlag locale={current} className="h-4" />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 gap-1">
        {languages.map((l) => (
          <LocaleOption key={l} locale={l} pathname={own[l] ?? pathname} current={l === current} />
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** A small note next to a language that is switched off but still holds text, so it is not lost silently. Nothing for an enabled language. */
export function LanguageOffMark({ language }: { language: string }) {
  const t = useTranslations("common");
  if (useEnabledLanguages().includes(language)) return null;
  return <span className="ms-1 text-xs font-normal text-muted-foreground">({t("languageOff")})</span>;
}
